import { useState, useEffect, useMemo } from 'react';
import { useExchangeRates } from '@/hooks/useExchangeRates.ts';

interface SecuritiesValues {
  totalValue: number;
  equityValue: number;
  debtValue: number;
  derivativesValue: number;
}

interface UseSecuritiesCurrencyConversionOptions {
  baseCurrency: string;
  values: SecuritiesValues;
}

interface UseSecuritiesCurrencyConversionResult {
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  convertedValues: SecuritiesValues;
  isConverting: boolean;
  conversionError: Error | null;
  conversionFailed: boolean;
  usingFallback: boolean;
}

const FALLBACK_KEY_PREFIX = 'securities_exchange_rates_fallback_';

function loadFallbackRates(baseCurrency: string): Record<string, number> | null {
  try {
    const stored = localStorage.getItem(`${FALLBACK_KEY_PREFIX}${baseCurrency}`);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed.rates ?? null;
  } catch {
    return null;
  }
}

function saveFallbackRates(baseCurrency: string, rates: Record<string, number>): void {
  try {
    localStorage.setItem(
      `${FALLBACK_KEY_PREFIX}${baseCurrency}`,
      JSON.stringify({ rates, baseCurrency, savedAt: new Date().toISOString() })
    );
  } catch {
    // Ignore storage errors.
  }
}

function applyConversion(
  values: SecuritiesValues,
  rates: Record<string, number>,
  targetCurrency: string,
  baseCurrency: string
): SecuritiesValues {
  if (targetCurrency === baseCurrency) return values;
  const rate = rates[targetCurrency];
  if (!rate) return values;
  return {
    totalValue: values.totalValue * rate,
    equityValue: values.equityValue * rate,
    debtValue: values.debtValue * rate,
    derivativesValue: values.derivativesValue * rate,
  };
}

/**
 * Hook for converting securities portfolio values to a selected currency.
 * Stores last successful exchange rates in localStorage as a fallback.
 * Retries fetching rates on mount when there is an error.
 */
export function useSecuritiesCurrencyConversion({
  baseCurrency,
  values,
}: UseSecuritiesCurrencyConversionOptions): UseSecuritiesCurrencyConversionResult {
  const [selectedCurrency, setSelectedCurrency] = useState<string>(baseCurrency);

  const { rates, isLoading, error, refetch } = useExchangeRates(baseCurrency);

  // Persist successful rates to localStorage for fallback use.
  useEffect(() => {
    if (rates?.rates) {
      saveFallbackRates(baseCurrency, rates.rates);
    }
  }, [rates, baseCurrency]);

  // Retry on mount when there is an error.
  useEffect(() => {
    if (error) {
      refetch();
    }
  }, [error, refetch]);

  // Rates are unavailable when not loading and no live rates present.
  const ratesUnavailable = !isLoading && !rates?.rates;

  // Determine fallback/failed state independently of selected currency,
  // so callers can show warnings even before a currency change is made.
  const fallbackRates = useMemo(
    () => (ratesUnavailable ? loadFallbackRates(baseCurrency) : null),
    [ratesUnavailable, baseCurrency]
  );
  const usingFallback = ratesUnavailable && fallbackRates !== null;
  const conversionFailed = ratesUnavailable && fallbackRates === null;

  const convertedValues = useMemo(() => {
    if (selectedCurrency === baseCurrency) return values;

    // Use live rates when available.
    if (rates?.rates) {
      return applyConversion(values, rates.rates, selectedCurrency, baseCurrency);
    }

    // Use localStorage fallback when live rates are unavailable.
    if (fallbackRates) {
      return applyConversion(values, fallbackRates, selectedCurrency, baseCurrency);
    }

    // No rates available — return original values.
    return values;
  }, [rates, fallbackRates, values, selectedCurrency, baseCurrency]);

  return {
    selectedCurrency,
    setSelectedCurrency,
    convertedValues,
    isConverting: isLoading,
    conversionError: error,
    conversionFailed,
    usingFallback,
  };
}
